import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PRIORITIES = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

export default function AddRequirementDialog({ open, onOpenChange, product, onAdded }) {
  const [qty, setQty] = React.useState(1);
  const [priority, setPriority] = React.useState("normal");
  const [notes, setNotes] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setQty(1);
      setPriority("normal");
      setNotes("");
    }
  }, [open]);

  const submit = async () => {
    if (!product) return;
    if (!qty || qty < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }
    setSaving(true);
    try {
      await api.post("/requirements", {
        product_id: product.id,
        quantity: Number(qty),
        priority,
        notes,
      });
      toast.success("Added to requirement");
      // Trigger sidebar count refresh
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
      onAdded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-requirement-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Mark as requirement</DialogTitle>
          <DialogDescription>
            Set the quantity and priority for this product to add it to your restock list.
          </DialogDescription>
        </DialogHeader>

        {product && (
          <div className="p-3 rounded-md bg-[hsl(var(--muted))] text-sm mb-3">
            <div className="font-medium">{product.product}</div>
            <div className="text-xs text-muted-foreground flex gap-3 mt-1 font-mono-data">
              <span>Auto: {product.auto_barcode}</span>
              {product.manual_barcode && <span>Manual: {product.manual_barcode}</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {product.brand_name} · {product.group_name}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quantity</Label>
            <Input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="font-mono-data mt-1"
              data-testid="req-qty-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="mt-1" data-testid="req-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value} data-testid={`priority-option-${p.value}`}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Notes (optional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1"
              rows={3}
              data-testid="req-notes-input"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="req-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="req-confirm-btn"
          >
            {saving ? "Saving…" : "Add to requirement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
